import React, { useState, useEffect } from 'react'
import { GoPlus } from 'react-icons/go'
import { FiMinus } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { FaArrowDown } from 'react-icons/fa'
import { IoReload } from 'react-icons/io5'

const StopLossPercent = () => {
    // States for inputs
    const [entryPrice, setEntryPrice] = useState('')
    const [stopLossPercent, setStopLossPercent] = useState('')
    const [positionSizePercent, setPositionSizePercent] = useState('')
    const [portfolioSize, setPortfolioSize] = useState('')
    const [amtPercentage, setAmtPercentage] = useState(0)
    const [stopAmount, setStopAmount] = useState(0)
    const { t } = useTranslation()

    // States for computed results
    const [stopLossValue, setStopLossValue] = useState(null)
    const [numberOfShares2, setNumberOfShares2] = useState(null)
    const [riskRows, setRiskRows] = useState([])
    const [upsideTarget, setUpsideTarget] = useState([])
    const [gain, setGain] = useState([])
    // useEffect to calculate values when any input changes
    useEffect(() => {
        const entryPriceValue = parseFloat(entryPrice) || 0
        const stopLossPercentValue = parseFloat(stopLossPercent) || 0
        // Calculate Stop Loss Value
        const stopLossValueCalculated =
            entryPriceValue - entryPriceValue * (stopLossPercentValue / 100)

        const positionSizePercentValue =
            parseFloat(positionSizePercent) || 0
        const portfolioSizeValue = parseFloat(portfolioSize) || 0
        const amtPosition = (portfolioSizeValue / 100) * positionSizePercentValue
        const stopAmount = (amtPosition * stopLossPercentValue) / 100
        const ns = Math.floor(amtPosition / entryPriceValue)

        setStopLossValue(stopLossValueCalculated.toFixed(2))
        setAmtPercentage(parseFloat(amtPosition.toFixed(2)) || 0)

        setStopAmount(stopAmount)
        setNumberOfShares2(ns)

        const r1 = entryPriceValue - stopLossValueCalculated
        const r2 = 2 * r1
        const r3 = 3 * r1
        const r4 = 4 * r1
        const r5 = 5 * r1
        const r6 = 6 * r1
        const riskRows = [
            r1,
            r2,
            r3,
            r4,
            r5,
            r6
        ]
        const upsideTarget = [
            "Upside Target",
            r2 + entryPriceValue,
            r3 + entryPriceValue,
            r4 + entryPriceValue,
            r5 + entryPriceValue,
            r6 + entryPriceValue,
        ]
        const gain = [
            "Gain",
            ((r2 + entryPriceValue) - entryPriceValue) / entryPriceValue * 100,
            ((r3 + entryPriceValue) - entryPriceValue) / entryPriceValue * 100,
            ((r4 + entryPriceValue) - entryPriceValue) / entryPriceValue * 100,
            ((r5 + entryPriceValue) - entryPriceValue) / entryPriceValue * 100,
            ((r6 + entryPriceValue) - entryPriceValue) / entryPriceValue * 100,
        ]
        setRiskRows(riskRows)
        setUpsideTarget(upsideTarget)
        setGain(gain)


    }, [entryPrice, stopLossPercent, positionSizePercent, portfolioSize])

    // Handle increment and decrement of input values
    const handleIncrease = (setter, value) => {
        setter((prevValue) => (parseFloat(prevValue) + 0.1).toFixed(1))
    }

    const handleDecrease = (setter, value) => {
        setter((prevValue) => (parseFloat(prevValue) - 0.1).toFixed(1))
    }

    const resetAllInput = () => {
        setGain([])
        setUpsideTarget([])
        setRiskRows([])
        setNumberOfShares2(null)
        setStopLossValue(null)
        setStopAmount(0)
        setAmtPercentage(0)
        setPortfolioSize("")
        setPositionSizePercent("")
        setStopLossPercent("")
        setEntryPrice("")
    }

    return (
        <>
            <div className='mb-1'>
                <p className='text-sm font-medium text-white mb-1'>{t("calculator.entryPrice")}</p>
                <div className="flex group border border-transparent hover:border-[#2c3354] rounded-xl overflow-hidden transition-colors">
                    <input
                        type='number'
                        value={entryPrice}
                        onChange={(e) => setEntryPrice(e.target.value)}
                        className='w-full py-3 px-4 text-white bg-[#171d33] focus:outline-none custom-number-input'
                    />
                    <div className="flex items-center justify-center bg-[#2c3354]">
                        <button
                            onClick={() => handleIncrease(setEntryPrice, entryPrice)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <GoPlus size={19} />
                        </button>
                        <button
                            onClick={() => handleDecrease(setEntryPrice, entryPrice)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <FiMinus size={19} />
                        </button>
                    </div>
                </div>
            </div>

            <div className='mb-1'>
                <p className='text-sm font-medium text-white mb-1'>{t("calculator.stopLossPercent")}</p>
                <div className="flex group border border-transparent hover:border-[#2c3354] rounded-xl overflow-hidden transition-colors">
                    <input
                        type='number'
                        value={stopLossPercent}
                        onChange={(e) => setStopLossPercent(e.target.value)}
                        className='w-full py-3 px-4 text-white bg-[#171d33] focus:outline-none custom-number-input'
                    />
                    <div className="flex items-center justify-center bg-[#2c3354]">
                        <button
                            onClick={() => handleIncrease(setStopLossPercent, stopLossPercent)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <GoPlus size={19} />
                        </button>
                        <button
                            onClick={() => handleDecrease(setStopLossPercent, stopLossPercent)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <FiMinus size={19} />
                        </button>
                    </div>
                </div>
            </div>

            <div className='mb-1'>
                <p className='text-sm font-medium text-white mb-1'>{t("calculator.positionSizePercent")}</p>
                <div className="flex group border border-transparent hover:border-[#2c3354] rounded-xl overflow-hidden transition-colors">
                    <input
                        type='number'
                        value={positionSizePercent}
                        onChange={(e) => setPositionSizePercent(e.target.value)}
                        className='w-full py-3 px-4 text-white bg-[#171d33] focus:outline-none custom-number-input'
                    />
                    <div className="flex items-center justify-center bg-[#2c3354]">
                        <button
                            onClick={() => handleIncrease(setPositionSizePercent, positionSizePercent)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <GoPlus size={19} />
                        </button>
                        <button
                            onClick={() => handleDecrease(setPositionSizePercent, positionSizePercent)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <FiMinus size={19} />
                        </button>
                    </div>
                </div>
            </div>

            <div className='mb-1'>
                <p className='text-sm font-medium text-white mb-1'>{t("calculator.portfolioSize")}</p>
                <div className="flex group border border-transparent hover:border-[#2c3354] rounded-xl overflow-hidden transition-colors ">
                    <input
                        type='number'
                        value={portfolioSize}
                        onChange={(e) => setPortfolioSize(e.target.value)}
                        className='w-full py-3 px-4 text-white bg-[#171d33] focus:outline-none custom-number-input'
                    />

                    <div className="flex items-center justify-center bg-[#2c3354]">
                        <button
                            onClick={() => handleIncrease(setPortfolioSize, portfolioSize)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <GoPlus size={19} />
                        </button>
                        <button
                            onClick={() => handleDecrease(setPortfolioSize, portfolioSize)}
                            className="text-white py-3 px-3 active:text-[#004f86] transition-colors"
                        >
                            <FiMinus size={19} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Results */}
            <div className="mt-6">
                <h2 className="text-xl font-semibold text-[#00baff] mb-4 border-b border-[#2c3354] pb-2 tracking-wide">
                    {t("calculator.title")}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-white">
                    <div>
                        <span className="font-medium">{t("calculator.amtPosition")}:</span>
                        <span className="ml-2">{Math.floor(amtPercentage)}</span>
                    </div>
                    <div>
                        <span className="font-medium">{t("calculator.numberOfShare")}:</span>
                        <span className="ml-2">{numberOfShares2 !== null ? numberOfShares2 : "N/A"}</span>
                    </div>
                    <div>
                        <span className="font-medium">{t("calculator.stopAmount")}:</span>
                        <span className="ml-2">{stopAmount}</span>
                    </div>
                    <div>
                        <span className="font-medium">{t("calculator.stopPrice")}:</span>
                        <span className="ml-2">{stopLossValue !== null ? stopLossValue : "N/A"}</span>
                    </div>
                    <div>
                        <span className="font-medium">{t("calculator.riskEquity")}:</span>
                        <span className="ml-2">{Math.abs((stopAmount / portfolioSize) * 100)}</span>
                    </div>
                </div>
            </div>

            {(riskRows.length > 0 && gain.length > 0 && upsideTarget.length > 0) && <div className="overflow-x-auto rounded-lg border border-[#2c3354] mt-6">

                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-[#004f86] text-white text-sm font-semibold">
                            {[1, 2, 3, 4, 5, 6].map((_, index) => (
                                <th key={index} className="p-3 text-left">
                                    {index + 1}R
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-white text-sm">
                        <tr className="bg-[#171d33] hover:bg-[#2c3354] transition">
                            {riskRows.map((val, i) => (
                                <td key={i} className="p-3">{val}</td>
                            ))}
                        </tr>
                        <tr className="bg-[#1e2544] hover:bg-[#2c3354] transition">
                            {upsideTarget.map((val, i) => (
                                <td key={i} className="p-3">{val}</td>
                            ))}
                        </tr>
                        <tr className="bg-[#171d33] hover:bg-[#2c3354] transition">
                            {gain.map((val, i) => (
                                <td key={i} className="p-3">{val} %</td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
            }
            {(riskRows.length > 0 && gain.length > 0 && upsideTarget.length > 0) &&

                <button
                    className='w-full mt-2 bg-[#004f86] text-white py-2 mb-1 rounded-full flex items-center justify-center gap-2 transition-opacity ease-linear hover:opacity-75 text-base font-medium'
                    onClick={resetAllInput}
                >
                    {t("calculator.startOver")}    <IoReload />
                </button>
            }

        </>

    )
}

export default StopLossPercent
